/**
 * Live screen / window / tab capture (getDisplayMedia) with live measurement,
 * freeze-frame and temporal averaging of N consecutive video frames.
 *
 * While the feed is live, frames are continuously sampled into image A so every
 * profile, metric and ROI statistic updates live. The sampling rate adapts to
 * the cost of recomputing the measurements (up to the video frame rate).
 */
import { effect, signal } from '@preact/signals';
import type { RawFrame } from '../core/types';
import { liveVideo } from '../view/viewState';
import { setFrame } from './actions';
import { busy, frames, notify, type SlotId } from './store';

export interface CaptureInfo {
  surface: string;
  width: number;
  height: number;
  frameRate: number;
  label: string;
}

export const captureState = signal<{ active: boolean; info: CaptureInfo | null; grabbing: number }>({
  active: false,
  info: null,
  grabbing: 0,
});
export const averageFrames = signal(8);
/** Update measurements from the live feed (when not frozen). */
export const liveMeasure = signal(true);
/** Achieved live measurement rate in Hz (0 when idle). */
export const liveRate = signal(0);

let stream: MediaStream | null = null;
let video: HTMLVideoElement | null = null;

export function captureSupported(): boolean {
  return !!navigator.mediaDevices?.getDisplayMedia;
}

export async function startCapture(opts: { preferCurrentTab?: boolean } = {}) {
  if (!captureSupported()) {
    notify('error', 'Screen capture is not supported in this browser (getDisplayMedia unavailable). Use Chrome or Edge.');
    return;
  }
  let s: MediaStream;
  try {
    s = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 30 }, width: { ideal: 8192 }, height: { ideal: 8192 } },
      audio: false,
      // Chromium-specific hints (ignored elsewhere).
      ...({ selfBrowserSurface: 'include', surfaceSwitching: 'include', preferCurrentTab: !!opts.preferCurrentTab } as object),
    } as DisplayMediaStreamOptions);
  } catch (e) {
    if ((e as Error).name !== 'NotAllowedError') notify('error', `Screen capture failed: ${(e as Error).message}`);
    return;
  }
  await captureFromStream(s);
}

/** Start live viewing and measurement of any video MediaStream (screen capture, or a canvas stream in tests). */
export async function captureFromStream(s: MediaStream) {
  if (stream) stopCapture();
  stream = s;
  const track = stream.getVideoTracks()[0];
  // Prefer sharpness over motion if the encoder has to choose.
  try {
    track.contentHint = 'detail';
  } catch {
    /* unsupported */
  }
  video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  await video.play();
  await new Promise<void>((res) => {
    if (video!.videoWidth) res();
    else video!.onloadedmetadata = () => res();
  });
  const st = track.getSettings() as MediaTrackSettings & { displaySurface?: string };
  captureState.value = {
    active: true,
    grabbing: 0,
    info: {
      surface: st.displaySurface ?? 'unknown',
      width: video.videoWidth,
      height: video.videoHeight,
      frameRate: st.frameRate ?? 0,
      label: track.label,
    },
  };
  liveVideo.value = video;
  startLiveLoop();
  track.addEventListener('ended', () => stopCapture());
  if (st.displaySurface === 'monitor') {
    const expected = Math.round(screen.width * devicePixelRatio);
    if (Math.abs(video.videoWidth - expected) > 2)
      notify(
        'warn',
        `Captured width ${video.videoWidth} px differs from the display's physical width (${expected} px): the capture is scaled, so pixel values are resampled.`,
      );
  }
}

export function stopCapture() {
  liveLoopToken++;
  liveRate.value = 0;
  // Keep the last live frame as a regular capture so measurements stay available.
  const a = frames.A.value;
  if (a && a.meta.origin === 'live')
    frames.A.value = { ...a, meta: { ...a.meta, origin: 'capture', name: `${a.meta.name} (last live frame)` } };
  stream?.getTracks().forEach((t) => t.stop());
  stream = null;
  if (video) video.srcObject = null;
  video = null;
  liveVideo.value = null;
  captureState.value = { active: false, info: null, grabbing: 0 };
}

/** Wait for the next distinct video frame. */
function nextFrame(v: HTMLVideoElement): Promise<void> {
  return new Promise((res) => {
    const rv = v as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
    if (rv.requestVideoFrameCallback) rv.requestVideoFrameCallback(() => res());
    else setTimeout(res, 40);
  });
}

/**
 * Grab `n` frames and average them. n = 1 yields exact 8-bit codes; n > 1
 * yields a float frame of mean codes / 255 (saturation only where every frame
 * was at 255).
 */
export async function freezeFrame(slot: SlotId = 'A', n = averageFrames.value) {
  const v = video;
  const info = captureState.value.info;
  if (!v || !info) return;
  // Freezing into A ends live measurement; the stream keeps running for further grabs.
  if (slot === 'A') {
    liveLoopToken++;
    liveRate.value = 0;
  }
  const w = v.videoWidth;
  const h = v.videoHeight;
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as OffscreenCanvasRenderingContext2D;
  const count = Math.max(1, Math.round(n));
  const sum = count > 1 ? new Float32Array(w * h * 3) : null;
  let last: Uint8ClampedArray | null = null;
  busy.value = `Capturing ${count} frame${count > 1 ? 's' : ''}…`;
  try {
    for (let k = 0; k < count; k++) {
      captureState.value = { ...captureState.value, grabbing: k + 1 };
      if (k > 0) await nextFrame(v);
      ctx.drawImage(v, 0, 0);
      last = ctx.getImageData(0, 0, w, h).data;
      if (sum)
        for (let i = 0, j = 0; i < last.length; i += 4, j += 3) {
          sum[j] += last[i];
          sum[j + 1] += last[i + 1];
          sum[j + 2] += last[i + 2];
        }
    }
  } finally {
    busy.value = null;
    captureState.value = { ...captureState.value, grabbing: 0 };
  }
  let data: Uint8Array | Float32Array;
  if (sum) {
    const k = 1 / (255 * count);
    for (let i = 0; i < sum.length; i++) sum[i] *= k;
    data = sum;
  } else {
    data = new Uint8Array(w * h * 3);
    for (let i = 0, j = 0; i < last!.length; i += 4, j += 3) {
      data[j] = last![i];
      data[j + 1] = last![i + 1];
      data[j + 2] = last![i + 2];
    }
  }
  const frame: RawFrame = {
    width: w,
    height: h,
    channels: 3,
    data,
    scale: sum ? 1 : 1 / 255,
    maxCode: sum ? 1 : 255,
    isFloat: !!sum,
    clipLevel: 1,
    meta: {
      name: `Capture ${new Date().toLocaleTimeString()}`,
      format: 'capture',
      origin: 'capture',
      bitDepth: 8,
      encoding: {
        transfer: 'srgb',
        primaries: 'srgb',
        source: 'capture',
        description: 'Screen capture: browser video frames are delivered as sRGB',
      },
      info: {
        Surface: info.surface,
        Source: info.label,
        Resolution: `${w} × ${h}`,
        'Frames averaged': `${count}`,
        'Frame rate': info.frameRate ? `${Math.round(info.frameRate)} fps` : '—',
      },
      warnings: [
        'Screen capture passes through the browser video pipeline (possible compression, chroma subsampling and scaling). Values describe encoded display intent, not emitted luminance.',
      ],
      loadedAt: new Date().toISOString(),
    },
  };
  setFrame(slot, frame);
  if (slot === 'A') {
    // Show the frozen frame instead of the live feed; keep the stream for more grabs.
    liveVideo.value = null;
  }
}

/** Return to the live view (and live measurement) without stopping the stream. */
export function resumeLive() {
  if (!video) return;
  liveVideo.value = video;
  startLiveLoop();
}

// ------------------------------------------------------------------ live measurement

let liveLoopToken = 0;
let liveCanvas: OffscreenCanvas | null = null;

// A non-live image put into A (file, paste, session, freeze) ends live mode so the
// live loop does not overwrite it; the stream keeps running and "Live" resumes.
effect(() => {
  const a = frames.A.value;
  if (!a || a.meta.origin === 'live' || !video || liveVideo.peek() !== video) return;
  liveLoopToken++;
  liveRate.value = 0;
  liveVideo.value = null;
});

function captureMeta(w: number, h: number, info: CaptureInfo | null): RawFrame['meta'] {
  return {
    name: 'Live capture',
    format: 'capture',
    origin: 'live',
    bitDepth: 8,
    encoding: {
      transfer: 'srgb',
      primaries: 'srgb',
      source: 'capture',
      description: 'Screen capture: browser video frames are delivered as sRGB',
    },
    info: {
      Surface: info?.surface ?? '—',
      Source: info?.label ?? '—',
      Resolution: `${w} × ${h}`,
      Mode: 'live (updating)',
    },
    warnings: [],
    loadedAt: new Date().toISOString(),
  };
}

/** Grab the current video frame as an exact 8-bit RGB frame. */
function grabLiveFrame(v: HTMLVideoElement): RawFrame {
  const w = v.videoWidth;
  const h = v.videoHeight;
  if (!liveCanvas || liveCanvas.width !== w || liveCanvas.height !== h) liveCanvas = new OffscreenCanvas(w, h);
  const ctx = liveCanvas.getContext('2d', { willReadFrequently: true }) as OffscreenCanvasRenderingContext2D;
  ctx.drawImage(v, 0, 0);
  const rgba = ctx.getImageData(0, 0, w, h).data;
  const data = new Uint8Array(w * h * 3);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
    data[j] = rgba[i];
    data[j + 1] = rgba[i + 1];
    data[j + 2] = rgba[i + 2];
  }
  return {
    width: w,
    height: h,
    channels: 3,
    data,
    scale: 1 / 255,
    maxCode: 255,
    isFloat: false,
    clipLevel: 1,
    meta: captureMeta(w, h, captureState.value.info),
  };
}

const nextPaint = () => new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

/**
 * Continuously sample the live feed into image A. Each update waits for the
 * UI to finish recomputing (two animation frames), and the next update is
 * delayed so measurement work uses at most about half of the time.
 */
function startLiveLoop() {
  const token = ++liveLoopToken;
  const v = video;
  if (!v) return;
  let last = performance.now();
  let rate = 0;
  const step = async () => {
    if (token !== liveLoopToken || video !== v || liveVideo.value !== v) return;
    if (liveMeasure.value && captureState.value.grabbing === 0 && v.videoWidth) {
      const t0 = performance.now();
      frames.A.value = grabLiveFrame(v);
      await nextPaint();
      if (token !== liveLoopToken) return;
      const cost = performance.now() - t0;
      const now = performance.now();
      const inst = 1000 / Math.max(1, now - last);
      last = now;
      rate = rate ? rate * 0.8 + inst * 0.2 : inst;
      liveRate.value = rate;
      setTimeout(waitFrame, Math.max(0, cost - 16));
    } else {
      liveRate.value = 0;
      setTimeout(waitFrame, 150);
    }
  };
  const waitFrame = () => {
    if (token !== liveLoopToken) return;
    const rv = v as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
    if (rv.requestVideoFrameCallback && !v.paused) rv.requestVideoFrameCallback(() => void step());
    else setTimeout(() => void step(), 33);
  };
  waitFrame();
}
